import React, { Component } from "react";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";

const DEFAULT_STATE = {
  photoIndex: -1,
  isOpen: false,
  images: [],
  enableZoom: false,
  onIndexChange: null,
};

/**
 * Wraps a page so it can open a full-screen image viewer.
 *
 * The wrapped component receives a `lightbox` prop:
 *   lightbox.show({ photoIndex, images, enableZoom?, onIndexChange? })
 *   lightbox.setSrc(images)
 *   lightbox.close()
 *
 * `onIndexChange` fires when the visitor pages through with the arrows or the
 * keyboard, so a caller that shows its own selected thumbnail can stay in step.
 */
const LightboxHOCWrapper = (Child) => {
  class LightboxHOC extends Component {
    state = {
      ...DEFAULT_STATE,
    };

    showImg = (state, callback) => {
      this.setState({ isOpen: true, ...state }, () => {
        callback && callback(this.state);
      });
    };

    handleCancel = () => {
      this.setState({ isOpen: false });
    };

    setSrc = (images) => {
      this.setState({ images });
    };

    /** Wrap around in both directions, and tell the caller where we landed. */
    moveTo = (offset) => {
      this.setState(
        ({ photoIndex, images }) => ({
          photoIndex: (photoIndex + offset + images.length) % images.length,
        }),
        () => {
          const { onIndexChange, photoIndex } = this.state;
          onIndexChange && onIndexChange(photoIndex);
        }
      );
    };

    lightbox = {
      show: this.showImg,
      setSrc: this.setSrc,
      close: this.handleCancel,
    };

    render() {
      const { photoIndex, images, isOpen, enableZoom } = this.state;

      // A single image has nowhere to go. Leaving next/prev defined would
      // render arrows that wrap around to the same picture.
      const hasMultiple = images.length > 1;

      return (
        <>
          <Child {...this.props} lightbox={this.lightbox} />
          {isOpen && images.length > 0 && (
            <Lightbox
              mainSrc={images[photoIndex]}
              nextSrc={
                hasMultiple ? images[(photoIndex + 1) % images.length] : undefined
              }
              prevSrc={
                hasMultiple
                  ? images[(photoIndex + images.length - 1) % images.length]
                  : undefined
              }
              // Without these the arrows render but do nothing.
              onMoveNextRequest={() => this.moveTo(1)}
              onMovePrevRequest={() => this.moveTo(-1)}
              enableZoom={enableZoom}
              imageTitle={
                hasMultiple ? `${photoIndex + 1} of ${images.length}` : undefined
              }
              onCloseRequest={this.handleCancel}
              reactModalStyle={{ overlay: { zIndex: 3000 } }}
            />
          )}
        </>
      );
    }
  }

  return LightboxHOC;
};

export default LightboxHOCWrapper;
